import {defaultPropertyDefinitions} from "@buildwithflux/constants";
import {
    applyImportDataToDocument,
    buildLocalSlug,
    ElementHelper,
    FileStorageNaming,
    IAssetData,
    IImportData,
    setElementsAndGenerateNodes,
} from "@buildwithflux/core";
import {ClientFunctionsAdapter} from "@buildwithflux/firebase-functions-adapter";
import {DocumentRepository} from "@buildwithflux/repositories";
import {
    AnyPcbNode,
    createNewDocumentData,
    createPartFromDocument,
    createPartVersionFromDocument,
    createRootFootprintNode,
    createRootNode,
    DocumentName,
    Enterprise,
    FullyDecoratedOrganizationMember,
    headVersionName,
    IAssetsMap,
    IDocumentData,
    IElementData,
    IPartData,
    IPartVersionData,
    IPropertiesMap,
    IPropertyData,
    IUserData,
    Organization,
    pcbLayoutRootNodeId,
    PcbNodesMap,
    PcbRuleSetsMap,
    PcbRuleValue,
    RuleKey,
    slugifyDocumentName,
} from "@buildwithflux/models";
import {guid} from "@buildwithflux/shared";
import isFluxFootprintFileName from "helpers/isFluxFootprintFileName";
import type {IImportedAssetBlob} from "modules/data_portability/importers/common/types";

import {DocumentStorage} from "./DocumentStorage";
import {FileStorage} from "./FileStorage.types";
import {PartStorage} from "./PartStorage";

/**
 * Pin data structure for part creation
 */
export interface PinData {
    id: string;
    number: string;
    name: string;
    type: string;
    description: string;
    section: string;
    pinGroup: string;
    busGroup: string;
    orientation: string;
    order: string;
    subsymbolDesignatorSuffix: string;
}

/**
 * Standardized inputs for creating a Part project
 */
/**
 * Pin layout type for terminal positioning
 */
export type PinLayoutType = "ic" | "default";

export interface PartProjectInputs {
    name: string;
    description?: string;
    pins: PinData[];
    properties: IPropertiesMap;
    assets: IAssetsMap;
    pcbNodes: PcbNodesMap<AnyPcbNode>;
    pcbRuleSets: PcbRuleSetsMap;
    owner: Pick<IUserData, "uid" | "handle">;
    organization?: Organization;
    enterprise?: Enterprise;
    /**
     * Optional package type for parametric footprint generation.
     * When provided, a footprint node with the package rule will be created
     * using the correct document.uid.
     */
    packageType?: string;
    /**
     * Optional pin layout type for terminal positioning.
     * "ic" uses IC convention (left column top-to-bottom, right column bottom-to-top).
     * "default" uses standard layout (both columns top-to-bottom).
     */
    pinLayout?: PinLayoutType;
}

/**
 * Result of part project creation
 */
export interface PartCreationResult {
    document: IDocumentData;
    part: IPartData;
    partVersion: IPartVersionData;
    documentUrl: string;
}

/**
 * Inputs for creating a part from an imported document.
 * Used by KiCad, Eagle, and Altium importers.
 */
export interface ImportedPartInputs {
    documentData: IDocumentData;
    assets: IImportedAssetBlob[];
    creator: IUserData;
    organizationMember: FullyDecoratedOrganizationMember | undefined;
    fileStorage: FileStorage;
    functionsAdapter: ClientFunctionsAdapter;
    /**
     * If true, this document is a host document (e.g., a schematic with local parts)
     * and should not be published as a part into the library.
     */
    isHostDocument?: boolean;
    /**
     * Import data for centralized description and properties handling.
     * When provided, the service will:
     * 1. Set the description (always, even if empty, to override the default placeholder)
     * 2. Build and set the 5 standard properties (MPN, Manufacturer, Part Type, Datasheet URL, Designator Prefix)
     * 3. Merge with any additional properties already on documentData
     *
     * This ensures consistent behavior across all importers (KiCad, Eagle, Altium) without duplicate code.
     */
    importData?: Partial<IImportData>;
}

/**
 * Layout constants for terminal positioning
 */
const TERMINAL_LAYOUT = {
    PIN_SPACING_Y: 40,
    LEFT_COLUMN_X: -100,
    RIGHT_COLUMN_X: 100,
} as const;

/**
 * Service for creating Part projects from standardized inputs.
 * This consolidates the logic for creating Part projects that was previously
 * duplicated across multiple places in the codebase.
 */
export class PartProjectCreationService {
    constructor(
        private readonly documentStorage: DocumentStorage,
        private readonly partStorage: PartStorage,
        private readonly documentRepository?: DocumentRepository,
    ) {}

    /**
     * Creates a complete Part project including document, part, and part version.
     *
     * @param inputs - The standardized inputs for creating the part
     * @returns The created document, part, part version, and document URL
     */
    async createPart(inputs: PartProjectInputs): Promise<PartCreationResult> {
        const document = await this.createDocumentFromInputs(inputs);

        await this.documentStorage.createDocument(document);

        const latestVersion = guid();
        const part = createPartFromDocument(document, latestVersion);
        const partVersion = createPartVersionFromDocument(document, part.uid, headVersionName);

        await this.partStorage.savePartAndPartVersion(part, partVersion);

        const ownerHandle = inputs.organization?.handle ?? inputs.owner.handle;
        const documentUrl = `/${ownerHandle}/${document.slug}`;

        return {
            document,
            part,
            partVersion,
            documentUrl,
        };
    }

    /**
     * Creates a part from an imported document (KiCad, Eagle, Altium, etc.).
     * This method handles:
     * - Uploading assets to file storage
     * - Generating unique name/slug via the functions adapter
     * - Creating the part and part version
     * - Persisting to Firestore
     *
     * @param inputs - The imported part inputs including document data and assets
     * @returns The created document, part, part version, and document URL
     */
    async createPartFromImportedDocument(inputs: ImportedPartInputs): Promise<PartCreationResult> {
        const {
            documentData,
            assets,
            creator,
            organizationMember,
            fileStorage,
            functionsAdapter,
            isHostDocument,
            importData,
        } = inputs;

        // Apply import data centrally to ensure consistent description and properties handling
        // across all importers (KiCad, Eagle, Altium). This is the single source of truth for:
        // 1. Setting description (always, even if empty, to override the default placeholder)
        // 2. Building the 5 standard properties (MPN, Manufacturer, Part Type, Datasheet URL, Designator Prefix)
        // 3. Merging with any additional properties already on documentData
        if (importData) {
            // Capture existing properties before applying import data, since applyImportDataToDocument
            // clears documentData.properties. This preserves any importer-extracted properties
            // (e.g., distributor part numbers, custom attributes) that were set during parsing.
            const existingProperties = {...documentData.properties};
            applyImportDataToDocument(documentData, importData, existingProperties);
        }

        const partVersionAssets: IAssetsMap = {};
        let previewStorageName: string | undefined;

        for (const asset of assets) {
            const createdAsset = await this.createAsset(fileStorage, asset, documentData);
            if (!createdAsset) {
                throw new Error(`Failed to create asset ${asset.filename}`);
            }
            partVersionAssets[createdAsset.uid] = createdAsset;
            if (!previewStorageName && createdAsset.fileType === "svg") {
                previewStorageName = createdAsset.storageName;
            }
        }

        const ownerHandle = organizationMember ? organizationMember.organization.handle : creator.handle;

        const {
            data: {name, slug},
        } = await functionsAdapter.getProjectName({
            ownerHandle,
            enterpriseOwnerHandle: organizationMember?.enterprise?.handle,
            requestedName: documentData.name.padEnd(DocumentName.minLength ?? 0, "_"),
        });

        documentData.name = name;
        documentData.slug = documentData.localToDocumentUid ? buildLocalSlug(slug) : slug;
        documentData.has_custom_name = true;

        const newVersion = guid();
        const newPart = createPartFromDocument(documentData, newVersion);
        const newPartVersion = createPartVersionFromDocument(documentData, newPart.uid, newVersion);

        newPartVersion.assets = partVersionAssets;
        documentData.assets = partVersionAssets;
        if (previewStorageName) {
            newPartVersion.preview_image = previewStorageName;
            newPartVersion.symbol_resource_file = previewStorageName;
            documentData.preview_image = previewStorageName;
        }

        // NOTE: The document must be created BEFORE the part version is saved.
        // The cloud sync function requires that the document is created and set first
        // before the part version is created. Otherwise it will overwrite the terminals.
        // This has to be addressed in a refactoring that the "terminal position on symbol"
        // has to be included as a property in the terminal elements on the document itself.
        // AsanaId: #1199526854252908
        if (isHostDocument) {
            await this.documentStorage.createDocument(documentData);
        } else {
            await this.documentStorage.createDocument({
                ...documentData,
                belongs_to_part_uid: newPart.uid,
            });
            await this.partStorage.savePartAndPartVersion(newPart, newPartVersion);
        }

        // Verify that the document's elements are readable before returning.
        // This prevents a race condition where the user navigates to the document
        // before Firestore has made the elements subcollection readable.
        const expectedElementCount = Object.keys(documentData.elements ?? {}).length;
        if (expectedElementCount > 0) {
            await this.verifyDocumentElementsReadable(documentData.uid, expectedElementCount);
        }

        const documentUrl = `/${ownerHandle}/${documentData.slug}`;

        return {
            document: documentData,
            part: newPart,
            partVersion: newPartVersion,
            documentUrl,
        };
    }

    /**
     * Creates an asset from an imported asset blob and uploads it to file storage.
     *
     * @param fileStorage - The file storage service
     * @param asset - The imported asset blob
     * @param partDocumentData - The document data for the part
     * @returns The created asset data, or undefined if the asset type is not supported
     */
    private async createAsset(
        fileStorage: FileStorage,
        asset: IImportedAssetBlob,
        partDocumentData: IDocumentData,
    ): Promise<IAssetData | undefined> {
        const storageName = FileStorageNaming.createDocumentAssetFilename(asset.filename);

        const defaults = {
            uid: guid(),
            name: asset.filename,
            rawName: asset.filename,
            storageName,
            label: partDocumentData.name,
            isDefault: false,
            isThumbnail: false,
            isFootprint: false,
            is3dModel: false,
            isPcbBoardShape: false,
            associatedSymbol: asset.associatedSymbol,
        };

        if (asset.filename.endsWith(".svg")) {
            await fileStorage.uploadFile(storageName, new Blob([asset.content]), "image/svg+xml", true);

            const createdAsset: IAssetData = {
                ...defaults,
                fileType: "svg",
                isDefault: asset.associatedSymbol ? false : true,
                isThumbnail: true,
            };
            if (asset.associatedSymbol) {
                createdAsset.associatedSymbol = asset.associatedSymbol;
            }
            return createdAsset;
        } else if (isFluxFootprintFileName(asset.filename)) {
            await fileStorage.uploadFile(storageName, new Blob([asset.content]), "application/octet-stream", true);

            return {
                ...defaults,
                fileType: "flx_ftp",
                isFootprint: true,
            };
        }

        return undefined;
    }

    /**
     * Creates a document from the standardized inputs without persisting it.
     * This is useful when you need to customize the document before saving.
     *
     * @param inputs - The standardized inputs for creating the part
     * @returns The created document data
     */
    async createDocumentFromInputs(inputs: PartProjectInputs): Promise<IDocumentData> {
        const document = createNewDocumentData(inputs.owner, inputs.organization, inputs.enterprise);

        document.name = inputs.name;
        document.has_custom_name = true;
        document.slug = slugifyDocumentName(inputs.name);

        if (inputs.description) {
            document.description = inputs.description;
        }

        document.properties = {...document.properties, ...inputs.properties};
        document.assets = {...document.assets, ...inputs.assets};

        if (Object.keys(inputs.pcbNodes).length > 0) {
            document.pcbLayoutNodes = {...document.pcbLayoutNodes, ...inputs.pcbNodes};
        }

        if (Object.keys(inputs.pcbRuleSets).length > 0) {
            document.pcbLayoutRuleSets = {...document.pcbLayoutRuleSets, ...inputs.pcbRuleSets};
        }

        // Create parametric footprint node with package rule using the correct document.uid
        if (inputs.packageType) {
            this.createParametricFootprintNodes(document, inputs.packageType);
        }

        if (inputs.pins.length > 0) {
            const terminalElements = await this.createTerminalElements(inputs.pins, inputs.owner.uid, inputs.pinLayout);

            for (const terminal of terminalElements) {
                document.elements[terminal.uid] = terminal;
            }

            setElementsAndGenerateNodes(document, terminalElements);
        }

        return document;
    }

    /**
     * Creates parametric footprint nodes with the package rule set on the footprint node.
     * This ensures the footprint node has the correct document.uid for the parametric
     * footprint generator to work correctly.
     *
     * @param document - The document to add the footprint nodes to
     * @param packageType - The package type (e.g., "QFN", "BGA", "SOIC")
     */
    private createParametricFootprintNodes(document: IDocumentData, packageType: string): void {
        // Ensure root node exists (it should already exist from createNewDocumentData)
        if (!document.pcbLayoutNodes[pcbLayoutRootNodeId]) {
            const rootNode = createRootNode();
            document.pcbLayoutNodes[rootNode.uid] = rootNode;
        }

        // Create footprint node with the correct document.uid
        const footprintNode = createRootFootprintNode(document.uid);

        // Set the package rule on the footprint node
        const packageRule: {key: RuleKey; value: PcbRuleValue; uid: string; order: number} = {
            key: "package",
            value: packageType,
            uid: guid(),
            order: 0,
        };
        footprintNode.pcbNodeRuleSet.package = packageRule;

        // Add footprint node to document
        document.pcbLayoutNodes[footprintNode.uid] = footprintNode;

        // Update root node's childUids to include the footprint node
        const rootNode = document.pcbLayoutNodes[pcbLayoutRootNodeId];
        if (rootNode && !rootNode.childUids.includes(footprintNode.uid)) {
            rootNode.childUids.push(footprintNode.uid);
        }
    }

    /**
     * Creates terminal elements from pin data.
     * This extracts the logic from the wizard's createPartService.ts.
     *
     * @param pins - Array of pin data to create terminals from
     * @param userUid - The UID of the user creating the terminals
     * @param pinLayout - Optional pin layout type ("ic" for IC convention, "default" for standard)
     * @returns Array of created terminal elements
     */
    private async createTerminalElements(
        pins: PinData[],
        userUid: string,
        pinLayout?: PinLayoutType,
    ): Promise<IElementData[]> {
        if (pins.length === 0) {
            return [];
        }

        const genesisTerminalPart = await this.partStorage.getGenesisTerminal();
        const terminalElements: IElementData[] = [];
        const halfCount = Math.ceil(pins.length / 2);
        const rightColumnCount = pins.length - halfCount;

        for (let index = 0; index < pins.length; index++) {
            const pin = pins[index];
            if (!pin) continue;

            const isLeftColumn = index < halfCount;
            const xPos = isLeftColumn ? TERMINAL_LAYOUT.LEFT_COLUMN_X : TERMINAL_LAYOUT.RIGHT_COLUMN_X;

            // Calculate Y position based on pin layout
            // Note: Y-axis increases upward in schematic coordinates, so higher Y = visually higher
            let yPos: number;
            if (isLeftColumn) {
                // Left column: always top to bottom (Pin 1 at top = highest Y)
                yPos = (halfCount - 1 - index) * TERMINAL_LAYOUT.PIN_SPACING_Y;
            } else {
                // Right column positioning depends on layout type
                const rightColumnIndex = index - halfCount;
                if (pinLayout === "ic") {
                    // IC convention: right column goes bottom to top
                    // Pin 5 at bottom (lowest Y), Pin 8 at top (highest Y)
                    yPos = rightColumnIndex * TERMINAL_LAYOUT.PIN_SPACING_Y;
                } else {
                    // Default: right column goes top to bottom (same as left column)
                    yPos = (rightColumnCount - 1 - rightColumnIndex) * TERMINAL_LAYOUT.PIN_SPACING_Y;
                }
            }

            // Left-side terminals need to be flipped so they face outward
            const terminalElement = ElementHelper.createNewElementData(
                genesisTerminalPart,
                {x: xPos, y: yPos, flip: isLeftColumn ? true : undefined},
                userUid,
            );

            terminalElement.label = pin.name || `Pin ${pin.number}`;

            if (pin.type) {
                const pinTypeProp: IPropertyData = {
                    uid: defaultPropertyDefinitions.pin_type.key,
                    name: defaultPropertyDefinitions.pin_type.label,
                    value: pin.type,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[defaultPropertyDefinitions.pin_type.key] = pinTypeProp;
            }

            if (pin.section) {
                const sectionProp: IPropertyData = {
                    uid: defaultPropertyDefinitions.section.key,
                    name: defaultPropertyDefinitions.section.label,
                    value: pin.section,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[defaultPropertyDefinitions.section.key] = sectionProp;
            }

            if (pin.pinGroup) {
                const pinGroupProp: IPropertyData = {
                    uid: defaultPropertyDefinitions.pin_group.key,
                    name: defaultPropertyDefinitions.pin_group.label,
                    value: pin.pinGroup,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[defaultPropertyDefinitions.pin_group.key] = pinGroupProp;
            }

            if (pin.busGroup) {
                const busGroupProp: IPropertyData = {
                    uid: defaultPropertyDefinitions.bus_group.key,
                    name: defaultPropertyDefinitions.bus_group.label,
                    value: pin.busGroup,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[defaultPropertyDefinitions.bus_group.key] = busGroupProp;
            }

            if (pin.orientation) {
                const orientationProp: IPropertyData = {
                    uid: defaultPropertyDefinitions.pin_orientation.key,
                    name: defaultPropertyDefinitions.pin_orientation.label,
                    value: pin.orientation,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[defaultPropertyDefinitions.pin_orientation.key] = orientationProp;
            }

            if (pin.order) {
                const orderProp: IPropertyData = {
                    uid: defaultPropertyDefinitions.terminal_order.key,
                    name: defaultPropertyDefinitions.terminal_order.label,
                    value: pin.order,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[defaultPropertyDefinitions.terminal_order.key] = orderProp;
            }

            if (pin.subsymbolDesignatorSuffix) {
                const subsymbolProp: IPropertyData = {
                    uid: defaultPropertyDefinitions.subsymbol_designator_suffix.key,
                    name: defaultPropertyDefinitions.subsymbol_designator_suffix.label,
                    value: pin.subsymbolDesignatorSuffix,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[defaultPropertyDefinitions.subsymbol_designator_suffix.key] = subsymbolProp;
            }

            const existingPinNumber = ElementHelper.getPinNumberProperty(terminalElement);
            if (existingPinNumber) {
                const pinNumberProp = {
                    ...existingPinNumber,
                    value: pin.number,
                    order: Object.keys(terminalElement.properties).length + 1,
                };
                terminalElement.properties[existingPinNumber.uid] = pinNumberProp;
            }

            terminalElements.push(terminalElement);
        }

        return terminalElements;
    }

    /**
     * Verifies that a document's elements are readable from Firestore.
     * This is used to ensure that after writing a document, the elements subcollection
     * is fully readable before returning to the caller. This prevents race conditions
     * where the user navigates to the document before Firestore has propagated the writes.
     *
     * @param documentUid - The UID of the document to verify
     * @param expectedElementCount - The expected number of elements
     * @param maxRetries - Maximum number of retry attempts (default: 3)
     * @param retryDelayMs - Delay between retries in milliseconds (default: 100)
     */
    private async verifyDocumentElementsReadable(
        documentUid: string,
        expectedElementCount: number,
        maxRetries = 3,
        retryDelayMs = 100,
    ): Promise<void> {
        // Skip verification if documentRepository is not available (e.g., in tests)
        if (!this.documentRepository) {
            return;
        }

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            const loadedDocument = await this.documentRepository.getDataWithoutMigrations(documentUid);
            const actualElementCount = Object.keys(loadedDocument?.elements ?? {}).length;

            if (actualElementCount >= expectedElementCount) {
                return;
            }

            if (attempt < maxRetries - 1) {
                await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            }
        }

        // Don't throw - the document was written, it just might not be immediately readable.
        // The user can refresh if needed. This is a best-effort verification.
    }
}
